import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny, request } from '../../api/client';
import { fetchDeviceFaults, fetchDeviceRecords } from '../../api/ops';
import { BarChart } from '../../ui/charts';
import { Card, Segmented } from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { SubjectRecords } from '../assets/AssetDrawer';

interface DailyRow {
  day: string;
  energy_wh: number | null;
  on_time_s: number | null;
  power_avg_w: number | null;
  dimmer_avg_pct: number | null;
  night_s: number | null;
  online_night_s: number | null;
  reported_night_h: number | null;
  alarms_opened: number | null;
}

type Series = 'energy' | 'hours' | 'power' | 'availability';

/** Daily history of a device from the persistent aggregates (energy, light-on hours, power, night availability). */
export function DeviceHistoryCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const [series, setSeries] = useState<Series>('energy');
  const [days, setDays] = useState('30');
  const from = new Date(Date.now() - Number(days) * 86_400_000).toISOString().slice(0, 10);
  const q = useQuery({
    queryKey: ['device-daily', id, days],
    queryFn: () => request<{ days: DailyRow[] }>('GET', `/api/v1/devices/${id}/daily?from=${from}`),
    enabled: canAny(me, 'telemetry.view'),
  });
  const value = (r: DailyRow): number | null => {
    if (series === 'energy') return r.energy_wh !== null ? r.energy_wh / 1000 : null;
    if (series === 'hours') return r.on_time_s !== null ? r.on_time_s / 3600 : null;
    if (series === 'power') return r.power_avg_w;
    if (!r.night_s) return null;
    return Math.min(
      100,
      (100 * Math.max(r.online_night_s ?? 0, (r.reported_night_h ?? 0) * 3600)) / r.night_s,
    );
  };
  const rows = q.data?.days ?? [];
  const unit = { energy: 'kWh', hours: 'h', power: 'W', availability: '%' }[series];
  return (
    <Card
      title={t('deviceOps.history')}
      actions={
        <select
          className="input"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          aria-label={t('deviceOps.range')}
        >
          {['30', '90', '365'].map((d) => (
            <option key={d} value={d}>
              {t('deviceOps.lastDays', { n: d })}
            </option>
          ))}
        </select>
      }
    >
      <Segmented<Series>
        label={t('deviceOps.series')}
        value={series}
        onChange={setSeries}
        options={(['energy', 'hours', 'power', 'availability'] as const).map((s) => ({
          value: s,
          label: t(`deviceOps.s.${s}`),
        }))}
      />
      <BarChart
        ariaLabel={t('deviceOps.history')}
        data={rows.map((r) => ({
          label: r.day.slice(5),
          value: value(r) ?? 0,
          title: `${r.day}: ${value(r) === null ? '—' : `${formatNumber(value(r)!, 2)} ${unit}`}`,
        }))}
        format={(v) => formatNumber(v, v < 10 ? 1 : 0)}
      />
      <p className="muted small" style={{ marginBottom: 0 }}>
        {t('deviceOps.historyHint')}
      </p>
    </Card>
  );
}

/** Detected conditions (faults) and whether the alarm policy raises them — presence, fault and alarm stay apart. */
export function DeviceFaultsCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['device-faults', id], queryFn: () => fetchDeviceFaults(id) });
  const items = q.data?.items ?? [];
  return (
    <Card title={t('deviceOps.faults')} bodyClass="">
      <div className="card-body muted small" style={{ paddingBottom: 0 }}>
        {t('deviceOps.faultsHint')}
      </div>
      <table className="table table-tight table-wrap">
        <tbody>
          {items.map((f) => (
            <tr key={f.id} style={f.cleared_at ? { opacity: 0.55 } : undefined}>
              <td>
                {t(`alarmTypes.${f.alarm_type}.name` as 'alarmTypes.device_offline.name', {
                  defaultValue: f.alarm_type,
                })}
                <span className="cell-sub mono">
                  {f.detector_key} {JSON.stringify((f.details as { value?: unknown }).value ?? null)}
                </span>
              </td>
              <td className="nowrap small">
                {formatDateTime(f.active_since)}
                {f.cleared_at && <span className="cell-sub">→ {formatDateTime(f.cleared_at)}</span>}
              </td>
              <td>
                {f.cleared_at ? (
                  <span className="pill">{t('deviceOps.cleared')}</span>
                ) : f.raised ? (
                  <span className="pill pill-warning">{t('deviceOps.raised')}</span>
                ) : (
                  <span className="pill">{t('deviceOps.notRaised')}</span>
                )}
              </td>
            </tr>
          ))}
          {!q.isLoading && items.length === 0 && (
            <tr>
              <td className="muted">{t('deviceOps.noFaults')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

/** GIS relations (pole, cabinet, …), maintenance notes and documents of the device. */
export function DeviceRecordsCard({ id, clientId }: { id: string; clientId: string | null }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['device-records', id], queryFn: () => fetchDeviceRecords(id) });
  if (!q.data) return null;
  return (
    <Card title={t('deviceOps.records')}>
      <SubjectRecords subjectType="device" subjectId={id} clientId={clientId} detail={q.data} />
    </Card>
  );
}
